import React, { FC, useState, useEffect } from "react";
import { Checkbox, Form } from "antd";
import InputFormItemComponent from "../../../../../../components/form/items/input";
import { GrowingList } from "../helpers";
import HiddenValueFormItemComponent from "../../../../../../components/form/items/hiddenValue";
import { I18n, Translate } from "react-redux-i18n";
import Divider from "../../../../../../components/divider";
import { SurveyLanguage, defaultTexts, Language } from "../../../../../../types/language";
import useSurveyDisabled from "../../../../../../hooks/useSurveyDisabled";
import CustomTemplate from ".././answerTemplates/custom";
import { TemplateEnum } from "../../../../../../types/templateEnum";
import SelectFormItemComponent from "../../../../../../components/form/items/select";

type ChoicesTemplateProps = {
  readonly langIndex: number;
  readonly lang: SurveyLanguage;
  readonly templateType: TemplateEnum;
};

const ChoicesTemplate: FC<ChoicesTemplateProps> = (props) => {
  const disabled = useSurveyDisabled();
  const [listLength, setListLength] = useState(0);
  const form = Form.useFormInstance();

  const [includeOtherOption, setIncludeOtherOption] = useState(() => {
    return form.getFieldValue(["answerTemplate", "addCustomAnswer"]) || false;
  });

  useEffect(() => {
    const addCustomAnswerFromForm = form.getFieldValue(["answerTemplate", "addCustomAnswer"]);
    const currentText = form.getFieldValue(["answerTemplate", "textTranslations", props.langIndex, "text"]);

    if (addCustomAnswerFromForm && !currentText) {
      const langCode = props.lang.langCode as Language;
      const defaultText = defaultTexts[langCode];
      if (defaultText) {
        form.setFieldValue(["answerTemplate", "textTranslations", props.langIndex, "text"], defaultText);
      }
    }
  }, [props.langIndex, props.lang.langCode, form, includeOtherOption]);

  return (
    <>
      <Divider translationCode={"answers"} />
      <GrowingList
        {...props}
        disabled={disabled}
        dataKey={"answerChoices"}
        addButtonLabel={I18n.t("addAnswerVariant")}
        onListLengthChange={setListLength}
      />

      <Form.Item shouldUpdate noStyle>
        {({ setFieldValue, getFieldValue }) => {
          const addCustomAnswer = getFieldValue(["answerTemplate", "addCustomAnswer"]);

          if (addCustomAnswer !== includeOtherOption) {
            setIncludeOtherOption(addCustomAnswer);
          }

          return (
            <div style={{ marginTop: 34, marginBottom: 20 }}>
              {props.templateType === TemplateEnum.MULTIPLE_CHOICE && (
                <SelectFormItemComponent
                  label={I18n.t("fixedNumberOfSelectedOptions")}
                  name={["answerTemplate", "maxOptionLimit"]}
                  values={Array.from({ length: listLength }, (_, i) => ({
                    label: i + 1,
                    value: i + 1,
                  }))}
                  style={{ width: "8em", textAlign: "center", paddingRight: "0.5em" }}
                />
              )}
              <CustomTemplate />
              <Checkbox
                disabled={disabled}
                checked={includeOtherOption}
                onChange={(e) => {
                  const isChecked = e.target.checked;

                  setIncludeOtherOption(isChecked);
                  setFieldValue(["answerTemplate", "addCustomAnswer"], isChecked);

                  if (isChecked) {
                    const langCode = props.lang.langCode as Language;
                    const defaultText = defaultTexts[langCode];
                    const currentText = getFieldValue(["answerTemplate", "textTranslations", props.langIndex, "text"]);

                    if (defaultText && !currentText) {
                      setFieldValue(["answerTemplate", "textTranslations", props.langIndex, "text"], defaultText);
                    }
                  } else {
                    setFieldValue(["answerTemplate", "textTranslations", props.langIndex, "text"], undefined);
                  }
                }}
                children={<Translate value={"includeYourChoiceInAnswers"} />}
              />
            </div>
          );
        }}
      </Form.Item>

      {includeOtherOption && (
        <div>
          <HiddenValueFormItemComponent
            name={["answerTemplate", "textTranslations", props.langIndex, "language"]}
            value={props.lang}
          />
          <InputFormItemComponent
            name={["answerTemplate", "textTranslations", props.langIndex, "text"]}
            label={I18n.t("titleForYourChoice")}
            hasFeedback
          />
        </div>
      )}
    </>
  );
};

export default ChoicesTemplate;
